import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface MartRow {
  readonly martId: string;
  readonly martCode: string;
  readonly martName: string;
  readonly domain: string;
  readonly status: string;
  readonly rowCount: number;
  readonly lastRefreshedAt: string | null;
}

const DOMAINS = ['', 'SALES', 'FINANCE', 'OPERATIONS', 'QUALITY', 'HR', 'EXECUTIVE'];
const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'DEPRECATED'];

/**
 * GR L26.01 — Data Marts (`/dwh/marts`). Curated, governed marts published on
 * top of the star schema. Each row exposes its business domain, publish
 * lifecycle, materialised row count and last refresh, and links into the
 * per-mart explorer (L26.02). Filterable by domain and lifecycle status.
 */
export function GrDataMartsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<MartRow[]>([]);
  const [domain, setDomain] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (domain) params.set('domain', domain);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: MartRow[] }>(`/v1/dwh/marts${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, domain, status]);

  return (
    <main aria-label="data-marts">
      <h1>{t('gr26.marts.title')}</h1>
      <div className="filters">
        <label>
          {t('gr26.marts.domain')}
          <select aria-label="filter-domain" value={domain} onChange={(e) => setDomain(e.target.value)}>
            {DOMAINS.map((d) => <option key={d || 'all'} value={d}>{d || t('gr26.marts.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr26.marts.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr26.marts.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr26.marts.loading')}</p> : (
        <>
          <p aria-label="mart-count">{t('gr26.marts.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr26.marts.empty')}</p> : (
            <table aria-label="mart-table">
              <thead><tr><th>{t('gr26.marts.code')}</th><th>{t('gr26.marts.name')}</th><th>{t('gr26.marts.domain')}</th><th>{t('gr26.marts.status')}</th><th>{t('gr26.marts.rows')}</th><th>{t('gr26.marts.refreshed')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.martId}>
                    <td>{m.martCode}</td><td>{m.martName}</td><td>{m.domain}</td>
                    <td>{m.status}</td><td>{m.rowCount.toLocaleString()}</td>
                    <td>{at(m.lastRefreshedAt)}</td>
                    <td><Link aria-label={`explore-${m.martId}`} to={`/dwh/marts/${m.martId}/explorer`}>{t('gr26.marts.explore')}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
